import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U3500';
import { doGetDocumentMeta, luHandleAppiError, type U3500DocumentMeta } from '../U3500Service';

export interface UseU3500DocumentMetaHandlersParams {
  onSuccess?: (meta: U3500DocumentMeta) => void;
  onError?: (error: unknown) => void;
}

/**
 * Handler : métadonnées document (uncPath, previewable) — GET U3500/documents/:id.
 */
export function useU3500DocumentMetaHandlers(params: UseU3500DocumentMetaHandlersParams = {}) {
  const { onSuccess, onError } = params;
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const loadMeta = useCallback(
    async (documentId: string): Promise<U3500DocumentMeta | null> => {
      if (!documentId?.trim()) {
        return null;
      }
      try {
        const meta = await doGetDocumentMeta(axios, documentId.trim());
        onSuccess?.(meta);
        return meta;
      } catch (error: unknown) {
        const err = U2000HandleApiError(
          error as Parameters<typeof U2000HandleApiError>[0],
          t,
          NS,
          luHandleAppiError
        );
        if (err.genericErrors?.length) {
          err.genericErrors.forEach((e) => toast.error(e.text, e.title));
        }
        onError?.(error);
        return null;
      }
    },
    [axios, onSuccess, onError, t, toast]
  );

  return { loadMeta };
}
